/* Ketcher.jsx  (UNIFIED — compound editor + rxn_enum reaction mode + molexar draw mode)
 *
 * A react component for Ketcher.
 *
 * Modes (props):
 *   (default)        single-molecule compound editor (change-syncs via onMolChange).
 *   reactionMode     rxn_enum: reaction + rgroup + manual-map tools; extract via
 *                    getReactionMolfile().
 *   drawMode         molexar: molecule + rgroup (attachment-point) tools, reaction
 *                    OFF; extract via getDrawnMolfile(). Supports multi-fragment +
 *                    [*] attachment points for linker/scaffold tasks.
 *
 * NOTE: this file is touched by both the feature/rxn-enum-job and feature/molexar-job
 * branches. This unified version supports both; use it when merging so the two
 * branches don't fight over Ketcher.jsx.
 */

import React from 'react';
import { IndigoWs } from './WebServices';
import { Loader } from './Loader';

export class Ketcher extends React.Component {
    constructor(props) {
        super(props);
        this.state = {};

        this.setup = this.setup.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.iframeRef = React.createRef();
        this.noteRef = React.createRef();
    }

    componentWillUnmount() {
        if (this.ketcher) {
            this.ketcher.editor.off('change', this.handleChange);
        }
    }

    async handleChange() {
        const { onMolChange } = this.props;

        if (this.ketcher) {
            let data = this.ketcher.getMolfile();
            try {
                data = await this.serverViewWS(data);
            } catch (err) {
                // Ignore error (will send coords without Hs to server)
            }
            onMolChange(data);
        }
    }

    async setup() {
        // Ketcher populates contentWindow.ketcher asynchronously, AFTER the iframe's
        // onLoad fires — poll for it so updateOptions() doesn't crash reading
        // `undefined.editor` (the intermittent init race).
        const win = this.iframeRef.current.contentWindow;
        const t0 = Date.now();
        while (!win.ketcher && Date.now() - t0 < 8000) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, 100));
        }
        const ketcher = win.ketcher;
        if (!ketcher) {
            this.updateNote('Sketcher failed to load');
            return;
        }
        const { startingMolText, reactionMode, drawMode } = this.props;
        this.updateOptions(ketcher);
        this.ketcher = ketcher;

        if (startingMolText) {
            let molText = startingMolText;
            try {
                molText = await this.layout(startingMolText);
                this.updateNote('');
            } catch (err) {
                console.error(`Exception in layout: ${err}`);
                this.updateNote('2D layout is unavailable, using flattened 3D view instead');
            }

            ketcher.setMolecule(molText);
        }
        // reaction/draw modes extract on submit (get*Molfile) and don't need the
        // single-molecule change round-trip (which also hits Indigo on every stroke).
        if (!reactionMode && !drawMode) {
            ketcher.editor.on('change', this.handleChange);
        }
    }

    /** RXN molfile of the drawn (manually-mapped) reaction — rxn_enum. */
    async getReactionMolfile() {
        return this.ketcher ? this.ketcher.getMolfile() : '';
    }

    /** MDL molfile of the drawn structure(s) — molexar. May be multi-fragment / hold [*]. */
    async getDrawnMolfile() {
        return this.ketcher ? this.ketcher.getMolfile() : '';
    }

    // I was not able to figure out how to specify inital settings for Ketcher,
    // so we need to update them after it is created.  Unfortunately, this does
    // not update the settings UI.
    updateOptions(ketcher) {
        const options = ketcher.editor.options();
        options.hideChiralFlag = true;
        ketcher.editor.options(options);
    }

    async serverViewWS(data) {
        return IndigoWs.toServerView(data);
    }

    async serverViewRDKit(data) {
        if (!Loader.RDKitExport) return '';
        const molA = await Loader.RDKitExport.getMol(data);
        const molB = await Loader.RDKitExport.getMol(molA.add_hs());
        const s = molB.get_new_coords(true);
        molA.delete();
        molB.delete();
        return s;
    }

    async layout(molText) {
        return this.layoutWS(molText);
    }

    async layoutWS(molText) {
        return IndigoWs.toSketcherView(molText);
    }

    async layoutRDKit(molText) {
        if (!Loader.RDKitExport) return '';
        const jsMol = Loader.RDKitExport.getCleanMol(molText);
        const s = jsMol.get_molblock();
        jsMol.delete();

        return s;
    }

    updateNote(text) {
        if (this.noteRef.current) {
            this.noteRef.current.innerText = text;
        }
    }

    render() {
        const { iframeId, reactionMode, drawMode } = this.props;
        const iframeStyle = { height: '99%', width: '100%', border: '1px solid darkgray' };
        // reactionMode: reaction + rgroup tools; drawMode: rgroup (attachment points),
        // reaction OFF; default: single-molecule compound editor.
        let disabledTools;
        if (reactionMode) {
            disabledTools = 'process,sgroup,sgroup-data,chiral-flag';
        } else if (drawMode) {
            disabledTools = 'process,sgroup,sgroup-data,reaction,chiral-flag';
        } else {
            disabledTools = 'process,rgroup,rgroup-data,sgroup,sgroup-data,reaction,chiral-flag';
        }
        const ketcherSrc = `ketcher/ketcher.html?api_path=/services/indigo/&disabledTools=${disabledTools}`;

        return (
            <span>
                <span
                    ref={this.noteRef}
                    style={{
                        position: 'absolute', right: '10px', top: '65px', color: 'lightbluesteel', fontStyle: 'italic',
                    }}
                />
                <iframe
                    id={iframeId}
                    title="Ketcher, a 2D Sketcher"
                    // See setup() / updateOptions() for Ketcher settings
                    src={ketcherSrc}
                    style={iframeStyle}
                    ref={this.iframeRef}
                    onLoad={this.setup}
                />
            </span>
        );
    }
}
